import React from 'react';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Checkbox } from '../../ui/checkbox';
import { formatPrice } from '../../../lib/utils';
import { CERTS } from '../../tax/TaxControls';

const Row = ({ k, v, strong, testId }) => (
  <div className={`flex justify-between ${strong ? 'font-semibold text-base border-t pt-2' : ''}`}><span>{k}</span><span className="font-mono" data-testid={testId}>{v}</span></div>
);

const Exemption = ({ ex, set }) => {
  const e = ex || { exempt: false, certificate_type: 'ST-120', certificate_on_file: false, reason: '', certificate_ref: '' };
  const up = (p) => set({ tax_exemption: { ...e, ...p } });
  return (
    <div className="space-y-2" data-testid="inv-exemption">
      <label className="flex items-center gap-2 text-sm"><Checkbox checked={e.exempt} onCheckedChange={(v) => up({ exempt: !!v })} data-testid="inv-exempt" /> Tax exempt (this invoice)</label>
      {e.exempt && <>
        <select value={e.certificate_type} onChange={(x) => up({ certificate_type: x.target.value })} className="w-full px-2 py-1 border rounded-md text-sm" data-testid="inv-exempt-cert">
          {Object.entries(CERTS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm"><Checkbox checked={e.certificate_on_file} onCheckedChange={(v) => up({ certificate_on_file: !!v })} data-testid="inv-exempt-onfile" /> Certificate on file</label>
        <Input placeholder="Exemption reason" value={e.reason} onChange={(x) => up({ reason: x.target.value })} data-testid="inv-exempt-reason" />
      </>}
    </div>
  );
};

const Override = ({ ov, set }) => {
  const o = ov || { active: false, rate: null, amount: null, reason: '' };
  const mode = o.amount != null ? 'amount' : 'rate';
  const up = (p) => set({ tax_override: { ...o, ...p } });
  return (
    <div className="space-y-2" data-testid="inv-override">
      <label className="flex items-center gap-2 text-sm"><Checkbox checked={o.active} onCheckedChange={(v) => up({ active: !!v })} data-testid="inv-override-on" /> Manual tax override</label>
      {o.active && <>
        <div className="flex gap-2">
          <select value={mode} onChange={(x) => up(x.target.value === 'amount' ? { amount: o.rate ?? 0, rate: null } : { rate: o.amount ?? 0, amount: null })} className="px-2 py-1 border rounded-md text-sm" data-testid="inv-override-mode">
            <option value="rate">Rate %</option><option value="amount">Amount $</option>
          </select>
          <Input type="number" step="0.001" value={(mode === 'amount' ? o.amount : o.rate) ?? ''} onChange={(x) => up({ [mode]: x.target.value === '' ? null : Number(x.target.value) })} className="w-28" data-testid="inv-override-value" />
        </div>
        <Textarea rows={2} placeholder="Reason (required)" value={o.reason} onChange={(x) => up({ reason: x.target.value })} data-testid="inv-override-reason" />
      </>}
    </div>
  );
};

export const TaxTotalsPanel = ({ form, set, calc, calcError }) => {
  const d = calc?.tax_details;
  const pending = d?.status === 'pending_address';
  return (
    <section className="grid md:grid-cols-2 gap-4" data-testid="inv-tax-panel">
      <div className="space-y-3 border rounded-md p-3"><Exemption ex={form.tax_exemption} set={set} /><Override ov={form.tax_override} set={set} /></div>
      <div className="border rounded-md p-3 space-y-1 text-sm" data-testid="inv-totals">
        {calcError && <p className="text-xs text-red-600" data-testid="inv-calc-error">{calcError}</p>}
        <Row k="Items" v={formatPrice(calc?.gross_subtotal || 0)} testId="inv-gross" />
        {calc?.discount_total > 0 && <Row k="Discounts" v={`−${formatPrice(calc.discount_total)}`} testId="inv-discounts" />}
        <Row k="Subtotal (taxable)" v={formatPrice(calc?.subtotal || 0)} testId="inv-subtotal" />
        <Row k={d?.rate != null && !pending ? `Tax (${d.rate}%)` : 'Tax'} v={pending ? 'Tax to be confirmed' : formatPrice(calc?.tax || 0)} testId="inv-tax" />
        <Row k="Total" v={formatPrice(calc?.total || 0)} strong testId="inv-total" />
        <p className={`text-xs pt-1 ${pending ? 'text-amber-700' : 'text-neutral-500'}`} data-testid="inv-tax-reason">{calc?.tax_reason || 'Add line items to calculate'}</p>
        {d?.jurisdiction && <p className="text-[11px] text-neutral-400">Code {d.jurisdiction.code} · {d.jurisdiction.matched_address}</p>}
      </div>
    </section>
  );
};
